/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/ExtensionSelectorModal.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Extensions selector modal.
────────────────────────────────────────────────────────────────*/

import { Search, Users, X } from 'lucide-react';

interface Extension {
  id: number | string;
  uuid: string;
  extension: string;
  name: string | null;
  enabled: boolean;
  class_of_service_name?: string;
}

interface ExtensionSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchResults: Extension[];
  selectedExtensions: Extension[];
  extensionSearch: string;
  isSearching: boolean;
  isLoadingAll: boolean;
  onSearchChange: (value: string) => void;
  onToggleExtension: (ext: Extension) => void;
  onSelectAll: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

export function ExtensionSelectorModal({
  isOpen, onClose, searchResults, selectedExtensions,
  extensionSearch, isSearching, isLoadingAll,
  onSearchChange, onToggleExtension, onSelectAll, t,
}: ExtensionSelectorModalProps) {
  if (!isOpen) return null;

  const handleClose = () => {
    onSearchChange('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">{t('bulkModification.selectExtensions')}</h3>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-border space-y-3">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 transition-all ${isSearching ? 'text-blue-600 dark:text-blue-400 animate-pulse' : 'text-muted-foreground'}`} />
            <input
              type="text"
              placeholder={t('bulkModification.searchPlaceholder')}
              value={extensionSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Select All Button */}
          <button
            onClick={onSelectAll}
            disabled={isLoadingAll}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingAll ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('bulkModification.loadingAllExtensions')}
              </>
            ) : (
              <>
                <Users className="h-4 w-4" />
                {t('bulkModification.selectAllExtensions')}
              </>
            )}
          </button>

          {extensionSearch && extensionSearch.length === 1 && (
            <p className="text-xs text-muted-foreground">{t('bulkModification.minCharsToSearch')}</p>
          )}
          {!extensionSearch && searchResults.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('bulkModification.showingAllExtensions')} ({searchResults.length})
            </p>
          )}
        </div>

        {/* Extensions List */}
        <div className="flex-1 overflow-y-auto p-4">
          {isSearching ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-3 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">{t('common.searching')}</p>
              </div>
            </div>
          ) : searchResults.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="space-y-2">
                <Search className="h-12 w-12 mx-auto text-muted-foreground/50" />
                <p className="text-sm">{extensionSearch ? t('bulkModification.noResults') : t('bulkModification.noExtensionsFound')}</p>
              </div>
            </div>
          ) : (
            searchResults.map((ext: Extension) => {
              const isSelected = selectedExtensions.some(e => (e.uuid || e.id) === (ext.uuid || ext.id));
              return (
                <button
                  key={ext.uuid || ext.id}
                  onClick={() => onToggleExtension(ext)}
                  className={`w-full text-left p-3 rounded-md mb-2 transition-colors border ${isSelected ? 'bg-primary/10 border-primary' : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-border'}`}
                >
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={isSelected} readOnly className="rounded border-border pointer-events-none" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-primary">{ext.extension}</span>
                        <span className="text-foreground">{ext.name || '-'}</span>
                        {isSelected && (
                          <span className="text-xs px-2 py-0.5 bg-primary text-primary-foreground rounded">{t('common.selected')}</span>
                        )}
                      </div>
                      {ext.class_of_service_name && (
                        <p className="text-sm text-muted-foreground">{ext.class_of_service_name}</p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {selectedExtensions.length} {t('bulkModification.extensionsSelected')}
          </span>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            {t('common.done')}
          </button>
        </div>
      </div>
    </div>
  );
}
